import { useTracker } from '@jetshop/core/analytics/Analytics';
import { trackListEvent } from '@jetshop/core/analytics/tracking';
import React, { useEffect, useMemo } from 'react';
import { css, cx } from 'linaria';
import { isAboveFold, priceStyle } from './ProductGrid';
import { WindowGrid } from '@jetshop/ui/WindowGrid';
import { theme } from '../Theme';
import loadable from '@loadable/component';
import LoadingPage from '../LoadingPage';

export const LoadableProductCard = loadable(() => import('./ProductCard'), {
  fallback: <LoadingPage />,
  resolveComponent: ProductCard => ProductCard.ProductCard
});

export const LoadableProductListCard = loadable(
  () => import('./ProductListCard'),
  {
    fallback: <LoadingPage />,
    resolveComponent: ProductListCard => ProductListCard.ProductListCard
  }
);

const container = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 0 -0.5em 1em;

  ${theme.below.lg} {
    margin: 0;
  }
`;

const defaultStyle = css`
  flex: 0 0 auto;
  width: 25%;
  height: auto !important;
`;

function ProductGridWindow({
  id,
  products,
  listName,
  loading,
  className,
  prevOffset,
  itemsPerRow,
  categoryPath,
  category = '',
  listGrid,
  ...rest
}) {
  // Track product listing
  const track = useTracker();
  useEffect(() => {
    // Dont track anything if there are no products to render
    if (!products || products.length === 0) return;

    // Otherwise track a list view event
    track(trackListEvent({ listName, products, category }));
  }, [listName, products, track, category]);

  const renderProduct = useMemo(
    () => ({ item: product, style, innerRef }) => (
      <div className={defaultStyle} style={style} ref={innerRef}>
        <LoadableProductCard
          key={product.articleNumber}
          product={product}
          as="div"
          categoryPath={categoryPath}
          loadImageEagerly={isAboveFold(product.index)}
        />
      </div>
    ),
    [categoryPath, listGrid]
  );

  const renderListProduct = useMemo(
    () => ({ item: product, style, innerRef }) => (
      <div className={defaultStyle} style={style} ref={innerRef}>
        <LoadableProductListCard
          key={product.articleNumber}
          product={product}
          as="div"
          categoryPath={categoryPath}
          loadImageEagerly={isAboveFold(product.index)}
        />
      </div>
    ),
    [categoryPath, listGrid]
  );

  const items = useMemo(() => {
    return products?.map((product, index) => ({ ...product, index }));
  }, [products]);

  return (
    <WindowGrid
      id={id}
      // itemsPerRow={[2, null, 3, 4]}
      itemsPerRow={listGrid ? [1] : [2, null, 3, 4]}
      items={items}
      prevOffset={prevOffset}
      className={cx(container, priceStyle, listGrid ? 'list' : 'grid')}
      component={listGrid ? renderListProduct : renderProduct}
    />
  );
}

export default ProductGridWindow;
